import type { ModelRegistry } from 'ember-data/types/registries/model';
import {
  type CategoryGroup,
  groupByCategory,
} from 'uplisting-frontend/utils/permission-settings';

export type PermissionRow = ModelRegistry['permission'];
export type PendingPermissions = ReadonlyMap<string, boolean>;

export interface TeammatePermissionGroup extends CategoryGroup<PermissionRow> {
  isOpen: boolean;
}

export interface GroupCounts {
  on: number;
  total: number;
  changed: number;
}

export function rootPermissions(rows: PermissionRow[]): PermissionRow[] {
  return rows.filter((row) => !row.parent);
}

export function hasKnownDefault(row: PermissionRow): boolean {
  return typeof row.permittedByDefault === 'boolean';
}

export function effectivePermitted(
  row: PermissionRow,
  pending: PendingPermissions,
): boolean {
  return pending.get(row.permission) ?? row.permitted;
}

export function isCustomised(
  row: PermissionRow,
  pending: PendingPermissions,
): boolean {
  return (
    hasKnownDefault(row) &&
    effectivePermitted(row, pending) !== row.permittedByDefault
  );
}

export function customisedRows(
  rows: PermissionRow[],
  pending: PendingPermissions,
): PermissionRow[] {
  return rows.filter((row) => isCustomised(row, pending));
}

export function resetToDefaults(rows: PermissionRow[]): Map<string, boolean> {
  const changes = new Map<string, boolean>();

  for (const row of rows) {
    if (hasKnownDefault(row) && row.permitted !== row.permittedByDefault) {
      changes.set(row.permission, row.permittedByDefault as boolean);
    }
  }

  return changes;
}

export function groupPermissions(
  rows: PermissionRow[],
  openCategories: ReadonlySet<string> | null,
): TeammatePermissionGroup[] {
  return groupByCategory(rows).map((group, index) => ({
    ...group,
    isOpen: openCategories ? openCategories.has(group.category) : index === 0,
  }));
}

export function groupCounts(
  group: CategoryGroup<PermissionRow>,
  pending: PendingPermissions,
): GroupCounts {
  return {
    on: group.rows.filter((row) => effectivePermitted(row, pending)).length,
    total: group.rows.length,
    changed: customisedRows(group.rows, pending).length,
  };
}
